/**
 * @file DownloadTranscriptLanguageModal.tsx
 *
 * @description This file provides a dialog interface for police officers to select a preferred language
 * before downloading a transcript.
 *
 * @module Modals
 * @requires React
 * @requires Material-UI
 */
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import {
  DEFAULT_DOWNLOAD_LANGUAGE,
  SUPPORTED_TRANSCRIPT_DOWNLOAD_LANGUAGES,
} from "../shared/constants/downloadLanguages";

/**
 * Props interface for the DownloadTranscriptLanguageModal component.
 * @interface DownloadTranscriptLanguageModalProps
 * @property {boolean} open - Controls the visibility of the modal dialog.
 * @property {() => void} onClose - Callback function to close the modal.
 * @property {(languageCode: string) => Promise<void>} onDownload - Async function that downloads
 *           the transcript in the selected language. The caller is responsible for closing the
 *           modal on success and surfacing errors, so the modal stays open for a retry on failure.
 */
interface DownloadTranscriptLanguageModalProps {
  open: boolean;
  onClose: () => void;
  onDownload: (languageCode: string) => Promise<void>;
}

/**
 * @name DownloadTranscriptLanguageModal
 *
 * @description Modal component that allows police officers to select a language before
 * downloading a transcript. Mirrors the DownloadReportModal language-selection flow.
 *
 * @param {DownloadTranscriptLanguageModalProps} props - Props for configuring the download modal behavior
 *
 * @returns {JSX.Element} A dialog component with language selection and download options
 */
export const DownloadTranscriptLanguageModal: React.FC<
  DownloadTranscriptLanguageModalProps
> = ({ open, onClose, onDownload }) => {
  // State to track the currently selected language code (defaults to English)
  const [selectedLanguageCode, setSelectedLanguageCode] = useState(
    DEFAULT_DOWNLOAD_LANGUAGE,
  );

  // State to track if a download is currently in progress
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadClick = () => {
    setIsDownloading(true);
    onDownload(selectedLanguageCode).finally(() => {
      setIsDownloading(false);
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <Typography>
          Please select a language to download the transcript.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", justifyContent: "center" }}>
        <Select
          fullWidth
          value={selectedLanguageCode}
          onChange={(e) => setSelectedLanguageCode(e.target.value)}
        >
          {SUPPORTED_TRANSCRIPT_DOWNLOAD_LANGUAGES.map(({ code, label }) => (
            <MenuItem key={code} value={code}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleDownloadClick}
          variant="contained"
          loading={isDownloading}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};
